"use client";
// Source-owned adaptation of beui.dev/components/blocks/bloom-menu.

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { EASE_OUT, SPRING_PANEL } from "../../lib/ease";
import { cn } from "../../lib/utils";
import { uiLibrarySourceAttrs } from "../../lib/ui-library/ui-library-source-attrs";

const FOCUSABLE =
  'button:not([disabled]),a[href],input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

interface MenuPosition {
  bottom: number;
  left: number;
  maxHeight: number;
  width: number;
}

export interface BloomMenuProps {
  open: boolean;
  onClose: () => void;
  anchor?: HTMLElement | null;
  anchorRect?: DOMRect | null;
  ariaLabel: string;
  children: ReactNode;
  width?: number;
  role?: "menu" | "dialog";
  className?: string;
}

export function BloomMenu({
  open,
  onClose,
  anchor,
  anchorRect,
  ariaLabel,
  children,
  width = 364,
  role = "dialog",
  className,
}: BloomMenuProps) {
  const reduce = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const [position, setPosition] = useState<MenuPosition | null>(null);

  const updatePosition = useCallback(() => {
    const rect = anchor?.getBoundingClientRect() ?? anchorRect;
    if (!rect || typeof window === "undefined") {
      setPosition(null);
      return;
    }
    const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const menuWidth = Math.min(width, viewportWidth - 24);
    setPosition({
      bottom: Math.max(12, viewportHeight - rect.top + 10),
      left: Math.min(
        Math.max(12, rect.right - menuWidth),
        Math.max(12, viewportWidth - menuWidth - 12),
      ),
      maxHeight: Math.max(72, rect.top - 22),
      width: menuWidth,
    });
  }, [anchor, anchorRect, width]);

  useEffect(() => {
    if (!open) return;
    returnFocusRef.current =
      anchor ??
      (document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null);
    updatePosition();
    const frame = window.requestAnimationFrame(() => {
      panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus({
        preventScroll: true,
      });
    });
    const handlePosition = () => updatePosition();
    window.addEventListener("resize", handlePosition);
    window.addEventListener("scroll", handlePosition, true);
    window.visualViewport?.addEventListener("resize", handlePosition);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", handlePosition);
      window.removeEventListener("scroll", handlePosition, true);
      window.visualViewport?.removeEventListener("resize", handlePosition);
      returnFocusRef.current?.focus({ preventScroll: true });
    };
  }, [anchor, open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || anchor?.contains(target))
        return;
      onClose();
    };
    window.addEventListener("pointerdown", closeOnOutsidePointer);
    return () =>
      window.removeEventListener("pointerdown", closeOnOutsidePointer);
  }, [anchor, onClose, open]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    if (
      role !== "menu" ||
      !["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)
    )
      return;
    const items = [
      ...(panelRef.current?.querySelectorAll<HTMLElement>(
        '[role="menuitem"]',
      ) ?? []),
    ];
    if (!items.length) return;
    event.preventDefault();
    const current = items.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? items.length - 1
          : (current + (event.key === "ArrowDown" ? 1 : -1) + items.length) %
            items.length;
    items[next]?.focus();
  };

  if (!position || typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence initial={false}>
      {open ? (
        <motion.div
          ref={panelRef}
          role={role}
          aria-label={ariaLabel}
          tabIndex={-1}
          data-fintheon-bloom-menu="active"
          {...uiLibrarySourceAttrs("beui")}
          initial={false}
          animate={reduce ? { y: 0, scale: 1 } : { y: 0, scale: 1 }}
          exit={reduce ? { y: 0, scale: 1 } : { y: 4, scale: 0.985 }}
          transition={
            reduce ? { duration: 0.16, ease: EASE_OUT } : SPRING_PANEL
          }
          onKeyDown={handleKeyDown}
          style={{
            bottom: position.bottom,
            left: position.left,
            maxHeight: position.maxHeight,
            position: "fixed",
            width: position.width,
            zIndex: 90,
          }}
          className={cn(
            "overflow-y-auto rounded-2xl border border-[color-mix(in_srgb,var(--fintheon-accent)_18%,transparent)] bg-[#090704] text-[var(--fintheon-text)] shadow-[0_10px_20px_color-mix(in_srgb,var(--fintheon-bg)_64%,transparent)]",
            className,
          )}
        >
          {children}
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
